import type { DiscoveredApi } from '../api-discovery/beacon-client.js'
import type { TokenStore } from '../auth/token-store.js'

import { authorizedFetch } from '../auth/authorized-fetch.js'

/** Everything the bootstrap needs from the outside world, injected for testability. */
export interface BootstrapEnv {
  /**
   * Run one discovery pass and return the adopted origin, or `null` if none earned trust. The
   * trust decision lives entirely inside this function (pinned-key signature verification) —
   * the bootstrap never inspects a domain itself, it only consumes an already-trusted result.
   */
  discover: () => Promise<DiscoveredApi | null>
  /** The underlying fetch the authorized wrapper drives (the global `fetch` in production). */
  fetch: typeof fetch
  /** Where the bearer token is read from for authorized calls. */
  store: TokenStore
}

/**
 * The terminal outcome of a boot. `unavailable` is a first-class, defined state — no candidate
 * in the probed window proved itself, so the app has no trusted API yet — deliberately distinct
 * from a hang or a throw so the shell can render a retry affordance instead of a blank screen.
 */
export type BootstrapResult = { api: RuntimeApi; status: 'ready' } | { status: 'unavailable' }

/**
 * The runtime API surface the app uses once boot-time discovery has adopted a trusted domain.
 * Every call is funnelled through `fetch`, which is a pre-bound `authorizedFetch` — so the app
 * never sees (or can bypass) the raw domain or the bearer-token plumbing.
 */
export interface RuntimeApi {
  /** The signature-verified, currently-live API origin. */
  apiDomain: string
  /** Call the discovered API with the bearer token attached and cookies omitted. */
  fetch: (path: string, init?: RequestInit) => Promise<Response>
  /** DGA index of `apiDomain`, so the caller can persist its position and resume from there. */
  index: number
}

/**
 * Discover a live, signature-verified API domain at launch and, on success, expose an API whose
 * every request is routed through `authorizedFetch` (bearer header, no cookies) against that
 * domain. No API domain is adopted and no request can be made until discovery returns a trusted
 * result; if none does, the boot resolves to a defined `unavailable` state rather than hanging.
 */
export const bootstrapRuntimeApi = async (env: BootstrapEnv): Promise<BootstrapResult> => {
  // discover() is documented to never hang, but an unforeseen throw inside it (e.g. an
  // IDB-touching or DGA-derive failure) must still land on the same defined `unavailable`
  // state rather than becoming an unhandled promise rejection — the shell relies on this
  // function never rejecting.
  const discovered = await env.discover().catch(() => null)
  if (!discovered) return { status: 'unavailable' }

  const api: RuntimeApi = {
    apiDomain: discovered.domain,
    index: discovered.index,
    fetch: (path, init): Promise<Response> =>
      authorizedFetch({ apiDomain: discovered.domain, fetch: env.fetch, store: env.store }, path, init),
  }

  return { api, status: 'ready' }
}
